/**
 * Hooks React Query pour les tickets de support - Utilisateurs
 * Design System v2
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ticketsAPI } from '../services/tickets.service';
import type { CreateTicketDto } from '../types/tickets.types';

const TICKETS_KEY = 'support-tickets';

/**
 * Hook pour lister mes tickets avec pagination
 */
export function useTickets(options: { page?: number; limit?: number; status?: string } = {}) {
  return useQuery({
    queryKey: [TICKETS_KEY, 'list', options],
    queryFn: () => ticketsAPI.list(options),
    staleTime: 30000,
  });
}

/**
 * Hook pour recuperer un ticket par ID
 */
export function useTicket(id: string) {
  return useQuery({
    queryKey: [TICKETS_KEY, 'detail', id],
    queryFn: () => ticketsAPI.getById(id),
    enabled: !!id,
  });
}

/**
 * Hook pour creer un ticket
 */
export function useCreateTicket() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateTicketDto) => ticketsAPI.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [TICKETS_KEY] });
    },
  });
}

/**
 * Hook pour ajouter un message a un ticket
 */
export function useAddTicketMessage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ ticketId, content }: { ticketId: string; content: string }) =>
      ticketsAPI.addMessage(ticketId, content),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [TICKETS_KEY, 'detail', variables.ticketId] });
      queryClient.invalidateQueries({ queryKey: [TICKETS_KEY, 'list'] });
    },
  });
}

/**
 * Hook pour fermer un ticket (client satisfait)
 */
export function useCloseTicket() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (ticketId: string) => ticketsAPI.close(ticketId),
    onSuccess: (_, ticketId) => {
      queryClient.invalidateQueries({ queryKey: [TICKETS_KEY, 'detail', ticketId] });
      queryClient.invalidateQueries({ queryKey: [TICKETS_KEY, 'list'] });
    },
  });
}

/**
 * Hook pour rouvrir un ticket ferme
 */
export function useReopenTicket() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (ticketId: string) => ticketsAPI.reopen(ticketId),
    onSuccess: (_, ticketId) => {
      queryClient.invalidateQueries({ queryKey: [TICKETS_KEY, 'detail', ticketId] });
      queryClient.invalidateQueries({ queryKey: [TICKETS_KEY, 'list'] });
    },
  });
}
